import React, { useContext, useMemo, useState, useCallback } from 'react';
import { CiterightContext } from './CiterightContextProvider';
import { Heading } from 'js/design_system/Heading';
import { Body } from 'js/design_system/Body';
import { Button } from 'js/design_system/Button';
import Divider from 'js/design_system/Divider';
import styled from 'astroturf/react';
import { TextButton } from 'js/design_system/TextButton';
import { Spinner } from 'js/design_system/Spinner';
import Badge from 'js/design_system/Badge';
import FilterPanel, { FilterSettings } from './components/FilterPanel';
import SortPanel, { SortSettings } from './components/SortPanel';
import WorkCard from './components/WorkCard';
import { WorkData } from './types';
import { TextArea } from 'js/design_system/forms/TextArea';
import { getDefaultSortSettings } from './utils/sortDefaults';
import { getPriorityLabel, getPriorityColor } from './utils/claimPriority';
import { filterStreamingWorks } from './utils/filterStreamingWorks';

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const ClaimPage = ({
  claimId,
  onBack,
  onNext,
  onPrevious,
  claimIndex,
  totalClaims,
  filterSettings,
  sortSettings,
  onFilterChange,
  onSortChange,
}: {
  claimId: string;
  onBack: () => void;
  onNext?: (() => void) | null;
  onPrevious?: (() => void) | null;
  claimIndex: number;
  totalClaims: number;
  filterSettings: FilterSettings;
  sortSettings: SortSettings | null;
  onFilterChange: (settings: FilterSettings) => void;
  onSortChange: (settings: SortSettings) => void;
}) => {
  const context = useContext(CiterightContext);
  const claim = useMemo(
    () => context?.report?.claims.find((c) => c.id === claimId),
    [context?.report?.claims, claimId],
  );

  // Edit query state and handlers
  const [isEditingQuery, setIsEditingQuery] = useState(false);
  const [editedQuery, setEditedQuery] = useState('');
  const [isSearching, setIsSearching] = useState<Record<string, boolean>>({});

  const handleEditQuery = useCallback(() => {
    if (claim?.search?.query) {
      setEditedQuery(claim.search.query);
      setIsEditingQuery(true);
    }
  }, [claim?.search?.query]);

  const handleSaveQuery = useCallback(() => {
    if (editedQuery.trim() && claim?.actions?.update_query) {
      const updateDeferred = claim.actions.update_query(editedQuery.trim());
      setIsEditingQuery(false);

      updateDeferred.promise.catch((error) => {
        console.error('Failed to update query:', error);
      });
    }
  }, [editedQuery, claim?.actions]);

  const handleCancelQuery = useCallback(() => {
    setEditedQuery('');
    setIsEditingQuery(false);
  }, []);

  const handleSearch = useCallback(() => {
    if (claim?.actions?.search) {
      setIsSearching((prev) => ({ ...prev, [claim.id]: true }));
      const searchDeferred = claim.actions.search();
      searchDeferred.promise
        .catch((error) => {
          console.error('Failed to search claim:', error);
        })
        .finally(() => {
          setIsSearching((prev) => ({ ...prev, [claim.id]: false }));
        });
    }
  }, [claim?.actions, claim?.id]);

  const handleNavigateToEvaluation = useCallback(() => {
    const reportId = context?.report?.report_id;
    if (!reportId || !claimId) return;

    // Build evaluation URL
    const params = new URLSearchParams(window.location.search);
    params.set('claim_id', claimId);
    params.set('report_id', reportId.toString());
    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.open(newUrl, '_blank', 'noopener,noreferrer');
  }, [context?.report?.report_id, claimId]);

  // Use report-level rerank data availability from context
  const hasValidRerankData = context?.hasAnyRerankData ?? false;

  // Filter and sort logic for suggested works
  const filteredAndSortedWorks = useMemo(() => {
    if (!claim?.search?.suggested_works) return [];

    // First, filter based on classification (show report works if any exist, otherwise fallbacks)
    const classificationFiltered = filterStreamingWorks(
      claim.search.suggested_works,
    );

    // Resolve sort settings - use user-defined or default
    const effectiveSortSettings: SortSettings =
      sortSettings || getDefaultSortSettings();

    const filterAndSortWorks = (works: WorkData[]): WorkData[] => {
      // Filter works based on user-selected filter settings
      const filtered = works.filter((work) => {
        const relevanceScore = (work.relevance_score ?? 0) * 100; // Convert to percentage
        const impactFactor = work.impact_factor?.impact_factor ?? 0;
        const citations = work.work.citationCount ?? 0;
        const workSource = work.source || 'unknown';

        const meetsRelevance = relevanceScore >= filterSettings.relevance.min;
        const meetsImpactFactor =
          impactFactor >= filterSettings.journalImpactFactor.min;
        const meetsCitations = citations >= filterSettings.citations.min;
        const meetsSource =
          filterSettings.sources.selected.includes(workSource);
        // Exclude preprints: only show works where isPublished === true
        // Works with isPublished === false (preprints) or undefined (no data) are filtered out
        const meetsPublicationStatus =
          !filterSettings.excludePreprints.enabled ||
          work.work.isPublished === true;

        return (
          meetsRelevance &&
          meetsImpactFactor &&
          meetsCitations &&
          meetsSource &&
          meetsPublicationStatus
        );
      });

      // Sort works based on settings
      if (effectiveSortSettings.field === 'relevance') {
        return filtered; // Keep original order for relevance
      }

      const sorted = [...filtered].sort((a, b) => {
        let aValue = 0;
        let bValue = 0;

        switch (effectiveSortSettings.field) {
          case 'citationCount':
            aValue = a.work.citationCount ?? 0;
            bValue = b.work.citationCount ?? 0;
            break;
          case 'impactFactor':
            aValue = a.impact_factor?.impact_factor ?? 0;
            bValue = b.impact_factor?.impact_factor ?? 0;
            break;
          case 'publicationYear':
            aValue = a.work.publicationYear ?? 0;
            bValue = b.work.publicationYear ?? 0;
            // Use citation count as tiebreaker when years are equal
            if (aValue === bValue) {
              const aCitations = a.work.citationCount ?? 0;
              const bCitations = b.work.citationCount ?? 0;
              // For tiebreaker, higher citations should always come first
              return bCitations - aCitations;
            }
            break;
          case 'work_index':
            aValue = a.work_index ?? Infinity;
            bValue = b.work_index ?? Infinity;
            break;
          case 'relevance_score':
            aValue = a.relevance_score ?? 0;
            bValue = b.relevance_score ?? 0;
            break;
        }

        if (effectiveSortSettings.direction === 'desc') {
          return bValue - aValue;
        } else {
          return aValue - bValue;
        }
      });

      return sorted;
    };

    return filterAndSortWorks(classificationFiltered);
  }, [claim?.search?.suggested_works, filterSettings, sortSettings]);

  if (!claim) {
    return (
      <div>
        <Body size="sm" style={{ color: 'var(--text-secondary)' }}>
          Claim not found.
        </Body>
        <Button size="xs" onClick={onBack} style={{ marginTop: 8 }}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <Section>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <TextButton icon="arrow_back" onClick={onBack}>
            Back to Triage
          </TextButton>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Body size="sm">
            {claimIndex + 1} of {totalClaims}
          </Body>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onPrevious && (
            <Button size="sm" onClick={onPrevious} icon="arrow_back" secondary>
              Previous
            </Button>
          )}
          {onNext && (
            <Button
              size="sm"
              onClick={onNext}
              iconRight="arrow_forward"
              secondary
            >
              Next
            </Button>
          )}
        </div>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 8,
        }}
      >
        {claim.reason && (
          <Body size="xs" style={{ color: 'var(--text-secondary)', flex: 1 }}>
            {claim.reason}
          </Body>
        )}
        {claim.priority && (
          <Badge
            text={getPriorityLabel(claim.priority, claim.top_claims_count)}
            backgroundColor={
              getPriorityColor(claim.priority, claim.top_claims_count)
                .backgroundColor
            }
            color={
              getPriorityColor(claim.priority, claim.top_claims_count).color
            }
            height="24px"
          />
        )}
      </div>
      <div
        style={{
          backgroundColor: 'var(--background-beige-light)',
          padding: '8px',
          borderLeft: `4px solid ${
            getPriorityColor(claim.priority, claim.top_claims_count)
              .backgroundColor || 'transparent'
          }`,
          marginBottom: 8,
        }}
      >
        <Body size="sm" style={{ fontStyle: 'italic' }}>
          "{claim.text}"
        </Body>
      </div>
      {claim.search?.message_to_user && (
        <Body size="xs" style={{ color: 'var(--text-secondary)' }}>
          {claim.search?.message_to_user}
        </Body>
      )}

      {context?.report?.report_id &&
        claim?.search?.suggested_works &&
        claim.search.suggested_works.length > 0 && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: 12,
            }}
          >
            <Button
              size="sm"
              onClick={handleNavigateToEvaluation}
              iconRight="open_in_new"
              secondary
            >
              Evaluate sources in detail
            </Button>
          </div>
        )}

      <Divider.s16 />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Heading size="sm" as="h3">
          Suggested references
        </Heading>
        <div style={{ display: 'flex', gap: '8px' }}>
          <FilterPanel
            onFilterChange={onFilterChange}
            currentFilters={filterSettings}
          />
          <SortPanel
            onSortChange={onSortChange}
            currentSort={sortSettings}
            hasRerankData={hasValidRerankData}
          />
        </div>
      </div>

      {claim.search?.query && (
        <div style={{ marginTop: 8, marginBottom: 16 }}>
          {isEditingQuery ? (
            <div>
              <Body size="sm" style={{ marginBottom: 4 }}>
                Search query:
              </Body>
              <TextArea
                value={editedQuery}
                onChange={(e) => setEditedQuery(e.target.value)}
                placeholder="Enter search query..."
                rows={3}
                style={{ marginBottom: 8 }}
              />
              <div style={{ display: 'flex', gap: 4 }}>
                <Button size="xs" onClick={handleSaveQuery}>
                  Update search
                </Button>
                <Button size="xs" onClick={handleCancelQuery}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <Body
                  size="sm"
                  style={{ margin: 0, flexShrink: 0, lineHeight: 1 }}
                >
                  Search query:
                </Body>
                <TextButton
                  size="sm"
                  iconRight="edit"
                  onClick={handleEditQuery}
                  style={{
                    margin: 0,
                    padding: 0,
                    height: 'auto',
                    lineHeight: 1,
                  }}
                >
                  Not seeing relevant suggestions? Refine your search query
                </TextButton>
              </div>
              <div
                onClick={handleEditQuery}
                style={{
                  border: '1px solid var(--stroke-light)',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  backgroundColor: 'var(--background-beige-light)',
                  cursor: 'pointer',
                  minHeight: '60px',
                }}
              >
                <Body
                  size="sm"
                  style={{ color: 'var(--text-secondary, #535366)' }}
                >
                  {claim.search.query}
                </Body>
              </div>
            </div>
          )}
        </div>
      )}
      {claim.search?.suggested_works === null && (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>
          <Button
            size="sm"
            onClick={handleSearch}
            iconRight="search"
            disabled={
              claim.search?.status === 'pending' || isSearching[claim.id]
            }
          >
            Search for citations
          </Button>
        </div>
      )}

      {context?.isPolling && !claim.search?.done && (
        <div style={{ textAlign: 'center', margin: '12px 0' }}>
          <Spinner size={20} />
          <Body
            size="xs"
            style={{ color: 'var(--text-secondary)', marginTop: 8 }}
          >
            Finding references...
          </Body>
        </div>
      )}

      {claim.search?.done &&
        claim.search?.suggested_works !== null &&
        filteredAndSortedWorks.length === 0 && (
          <div style={{ textAlign: 'center', margin: '12px 0' }}>
            <Body size="md" style={{ color: 'var(--text-secondary)' }}>
              No references found for this query.
            </Body>
          </div>
        )}

      {filteredAndSortedWorks.length > 0 && (
        <div
          style={{
            background: 'var(--background-white, #ffffff)',
            border: '1px solid var(--stroke-light)',
            borderRadius: '8px',
            padding: '0 12px',
          }}
        >
          {filteredAndSortedWorks.map((w, index) => (
            <WorkCard
              key={w.work.id + '#' + index}
              workData={w}
              showAddToListButton={true}
              claimId={claim?.id}
              claimText={claim?.text}
              pageContext="inline_report"
            />
          ))}
        </div>
      )}
    </Section>
  );
};
